import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Clock, UserCheck, Send, Zap, X, AlertCircle } from "lucide-react";

/**
 * Owner First-Look Priority Queue
 * - Lists bookings held for the owner (status="pending_owner_review")
 * - Owner picks a specific driver to delegate to OR releases to the driver pool
 * - Every job auto-releases after the server-side timer (see backend OWNER_REVIEW_MINUTES).
 */
export default function PriorityQueuePanel() {
  const [queue, setQueue] = useState([]);
  const [activeBooking, setActiveBooking] = useState(null); // opens delegate modal
  const [now, setNow] = useState(Date.now());

  const load = async () => {
    try {
      const { data } = await api.get("/admin/priority-queue");
      setQueue(data);
    } catch (e) {
      // silent — sweeper polling
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    const c = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, []);

  const release = async (bid) => {
    try {
      await api.post(`/admin/bookings/${bid}/release`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to release");
    }
  };

  return (
    <div className="space-y-4" data-testid="priority-queue-panel">
      <div className="bg-asap-yellow/20 border-2 border-asap-yellow p-4 flex items-start gap-3">
        <AlertCircle className="shrink-0 text-asap-orange" size={22}/>
        <div>
          <div className="font-display uppercase text-asap-deep text-sm">Owner First-Look Queue</div>
          <div className="text-xs text-slate-700 mt-1">
            Every new job lands here first. You have <strong>5 minutes</strong> to accept-yourself, delegate to a specific driver, or release to the pool.
            A courtesy SMS reminder is sent at the 3-minute mark. If you're unavailable, jobs release automatically — drivers are never told you passed.
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display uppercase text-asap-deep flex items-center gap-2"><Zap size={18}/> In Queue ({queue.length})</h3>
        </div>
        {queue.length === 0 && (
          <div className="p-10 text-center text-slate-400 text-sm">No jobs waiting on you right now. New requests will appear here first.</div>
        )}
        <div className="divide-y divide-slate-100">
          {queue.map((b) => (
            <PriorityRow key={b.id} b={b} now={now} onDelegate={() => setActiveBooking(b)} onRelease={() => release(b.id)} />
          ))}
        </div>
      </div>

      {activeBooking && (
        <DelegateModal booking={activeBooking} onClose={() => setActiveBooking(null)} onDone={async () => { setActiveBooking(null); await load(); }} />
      )}
    </div>
  );
}

function PriorityRow({ b, now, onDelegate, onRelease }) {
  const expiresAt = b.owner_review_expires_at ? new Date(b.owner_review_expires_at).getTime() : null;
  const remainingMs = expiresAt ? Math.max(0, expiresAt - now) : 0;
  const mm = Math.floor(remainingMs / 60000);
  const ss = Math.floor((remainingMs % 60000) / 1000).toString().padStart(2, "0");
  const isCritical = remainingMs < 60_000;
  return (
    <div className="px-3 sm:px-5 py-3 sm:py-4 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-3" data-testid={`pq-row-${b.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-display text-base sm:text-lg text-asap-deep">{b.service_name}</span>
          <span className="text-asap-orange font-display text-base sm:text-lg">${Number(b.price).toFixed(2)}</span>
          <span className={`ml-auto sm:hidden px-2 py-1 border-2 font-bold text-[10px] uppercase tracking-widest flex items-center gap-1 ${isCritical ? "border-asap-red text-asap-red animate-pulse" : "border-asap-deep text-asap-deep"}`} data-testid={`pq-timer-${b.id}`}>
            <Clock size={10}/> {mm}:{ss}
          </span>
        </div>
        <div className="text-xs text-slate-600 mt-0.5 truncate">
          <strong>{b.customer_name || "Customer"}</strong> · {b.pickup_address || `${b.pickup_lat?.toFixed?.(3)}, ${b.pickup_lng?.toFixed?.(3)}`}
        </div>
        {b.notes && <div className="text-xs text-slate-500 italic mt-0.5 line-clamp-2">"{b.notes}"</div>}
      </div>
      <div className={`hidden sm:flex px-3 py-1.5 border-2 font-bold text-xs uppercase tracking-widest items-center gap-1.5 ${isCritical ? "border-asap-red text-asap-red animate-pulse" : "border-asap-deep text-asap-deep"}`}>
        <Clock size={12}/> {mm}:{ss}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
        <button onClick={onDelegate} data-testid={`pq-delegate-${b.id}`} className="px-3 py-2 bg-asap-yellow text-asap-deep border-2 border-asap-deep font-bold uppercase text-xs tracking-wider flex items-center justify-center gap-1.5 hover:translate-x-[1px] hover:translate-y-[1px] transition">
          <Send size={13}/> Delegate
        </button>
        <button onClick={onRelease} data-testid={`pq-release-${b.id}`} className="px-3 py-2 bg-white text-asap-deep border-2 border-asap-deep font-bold uppercase text-xs tracking-wider hover:bg-asap-deep hover:text-white transition">
          Release
        </button>
      </div>
    </div>
  );
}

function DelegateModal({ booking, onClose, onDone }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/eligible-drivers/${booking.id}`);
        setDrivers(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Could not load drivers");
      } finally { setLoading(false); }
    })();
  }, [booking.id]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return drivers;
    return drivers.filter((d) => (d.name || "").toLowerCase().includes(s) || (d.email || "").toLowerCase().includes(s) || (d.phone || "").includes(s));
  }, [drivers, q]);

  const assign = async (driverId) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/bookings/${booking.id}/assign`, { driver_id: driverId });
      await onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="delegate-modal" onClick={onClose}>
      <div className="bg-white border-2 border-asap-deep max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b-2 border-asap-deep flex items-center justify-between bg-asap-deep text-white">
          <div className="min-w-0">
            <div className="font-display uppercase text-sm">Delegate {booking.service_name}</div>
            <div className="text-xs text-white/70 truncate">{booking.customer_name} · ${Number(booking.price).toFixed(2)} · {booking.pickup_address}</div>
          </div>
          <button onClick={onClose} data-testid="delegate-close" className="p-1 hover:bg-white/20"><X size={18}/></button>
        </div>
        <div className="p-4">
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="delegate-search" placeholder="Search by name, email, or phone" className="w-full px-3 py-2 border-2 border-asap-deep text-sm" />
          {err && <div className="mt-2 text-xs text-asap-red border border-asap-red p-2">{err}</div>}
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading && <div className="text-center py-8 text-slate-400 text-sm">Loading eligible drivers…</div>}
          {!loading && filtered.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">No matching drivers.</div>}
          <div className="divide-y divide-slate-200">
            {filtered.map((d) => (
              <div key={d.id} className="py-3 flex items-center gap-3" data-testid={`delegate-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-asap-deep">{d.name || d.email}</span>
                    {d.online ? <span className="text-[9px] uppercase tracking-widest bg-emerald-100 text-emerald-700 px-1.5 py-0.5 border border-emerald-500 font-bold">Online</span>
                              : <span className="text-[9px] uppercase tracking-widest bg-slate-100 text-slate-500 px-1.5 py-0.5 border border-slate-400 font-bold">Offline</span>}
                    {d.tier_id && <span className="text-[9px] uppercase tracking-widest bg-asap-yellow/40 text-asap-deep px-1.5 py-0.5 border border-asap-deep font-bold">{d.tier_id}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{d.vehicle_category || "—"} · {d.phone || "no phone"} · ★ {Number(d.rating || 5).toFixed(2)}</div>
                </div>
                <button disabled={busy} onClick={() => assign(d.id)} data-testid={`delegate-pick-${d.id}`} className="px-3 py-1.5 bg-asap-orange text-white font-bold uppercase text-[11px] tracking-wider border-2 border-asap-deep hover:translate-x-[1px] hover:translate-y-[1px] transition disabled:opacity-50 flex items-center gap-1.5">
                  <UserCheck size={12}/> Assign
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
